import { getStandardPageMatchForOptionId } from './global-ai-assistant-standard-page-match';

/**
 * Deterministic tool → step-title map for the assistant's reasoning trace.
 * Titles are short, present-tense clauses derived purely from the tool name
 * (and, for navigate, its target) — no model call, so they cost zero latency.
 */
const TITLES: Record<string, string> = {
  search_filters: 'Finding the right filters',
  // Generic on purpose: this step decides the metric/filters before we know
  // whether the result is a chart or a single-value answer, so it must read
  // right either way (render_chart then specialises to chart vs. figure).
  generate_chart_config: 'Preparing the answer',
  render_chart: 'Rendering the chart',
  apply_filters: 'Applying filters',
  // Server-side workforce read. Renders nothing to the user, so this step is
  // the ONLY trace that it happened — without an entry here it fell through to
  // the generic 'Working', which named neither the action nor its subject.
  query_workforce_data: 'Reading workforce data',
  // Same reasoning, second family: a server-side read renders nothing, so the
  // step title is the only trace the user has that it happened.
  query_sentiment_data: 'Reading sentiment data',
  // Third family, same reasoning again.
  query_transitions_data: 'Reading transitions data',
  // Fourth family, same reasoning again.
  query_postings_data: 'Reading postings data',
  // Fifth family, same reasoning again.
  query_pay_data: 'Reading pay data',
  query_company_ranking: 'Reading the company ranking',
  // The tool only OFFERS to build (it appends a confirm chip); the actual build
  // runs on click, with its own progress. So this step is pre-build.
  create_custom_page: 'Planning the dashboard',
  // Offers both a templated page and a build-a-dashboard option (two chips);
  // pre-build like create_custom_page.
  offer_page_or_custom: 'Finding the right page',
  update_current_page: 'Updating the dashboard',
  resolve_entity_scope: 'Identifying the entity',
  select_report: 'Finding a matching report',
  extract_report_params: 'Finding a matching report',
  offer_report: 'Offering reports',
  ask_user: 'Asking a question',
};

export function toolStepTitle(
  name: string,
  input?: unknown,
  options?: { answerAsText?: boolean }
): string {
  // Single-value answer: render_chart runs headlessly (no card) to fetch the
  // figure, so "Rendering the chart" reads wrong — relabel it. (generate_chart_config
  // is the step that DECIDES answerAsText, so its title can't know yet.)
  if (name === 'render_chart' && options?.answerAsText) {
    return 'Looking up the figure';
  }
  if (name === 'navigate') {
    const optionId =
      input && typeof input === 'object' && 'optionId' in input
        ? String((input as { optionId: unknown }).optionId)
        : undefined;
    if (!optionId) {
      return 'Navigating';
    }
    // Resolve the raw `__std__:{entity}:{segment}` candidate id to a human page
    // name (e.g. "Individual Discovery", "Company Compensation") so the
    // reasoning trace never leaks an internal id like
    // "__std__:app:talent-discovery".
    const pageName = getStandardPageMatchForOptionId(optionId)?.name;
    return pageName ? `Opening ${pageName}` : 'Navigating';
  }
  return TITLES[name] ?? 'Working';
}
